import React, { useEffect, useMemo, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Modal, Spin } from "antd";
import ReactStars from "react-stars";
import { toast, Toaster } from "react-hot-toast";
import Pagination from "components/pagination";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import NontAuthorized401 from "NontAuthorized401";
import {
  getAdminReview,
  getReviewsByPartnerId,
  listAdminReviews,
  updateAdminReviewStatus,
} from "./reviewApi";
import {
  formatReviewDate,
  getCustomerName,
  getPartnerName,
  getRatingValue,
  getReviewText,
  getReviewTitle,
  getServiceName,
  getStatusMeta,
} from "./reviewUtils";

const MEDIA_BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "";

const resolveReviewMediaUrl = (path) => {
  if (!path) return "";

  const trimmedPath = String(path).trim();
  if (!trimmedPath) return "";
  if (/^https?:\/\//i.test(trimmedPath)) return trimmedPath;

  const normalizedBase = MEDIA_BASE_URL.replace(/\/+$/, "");
  const normalizedPath = trimmedPath.replace(/^\/+/, "");

  return normalizedBase ? `${normalizedBase}/${normalizedPath}` : normalizedPath;
};

const renderReviewMedia = (mediaItems = []) => {
  if (!Array.isArray(mediaItems) || mediaItems.length === 0) {
    return <span className="text-sm text-gray-400">No media</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {mediaItems.map((item, index) => {
        const mediaUrl = resolveReviewMediaUrl(item?.media_url);

        if (!mediaUrl) return null;

        return item?.media_type === "video" ? (
          <div
            key={item?.id || mediaUrl}
            className="w-[150px] rounded-lg border border-gray-200 bg-gray-50 p-2"
          >
            <video controls preload="none" className="h-24 w-full rounded-md bg-black object-cover">
              <source src={mediaUrl} />
            </video>
            <a
              href={mediaUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 block truncate text-[11px] font-medium text-blue-600 hover:underline"
            >
              {`Video ${index + 1}`}
            </a>
          </div>
        ) : (
          <a
            key={item?.id || mediaUrl}
            href={mediaUrl}
            target="_blank"
            rel="noreferrer"
            className="block w-[92px] overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-1 hover:border-blue-400"
          >
            <img
              src={mediaUrl}
              alt={`Review media ${index + 1}`}
              className="h-20 w-full rounded-md object-cover"
            />
            <span className="mt-1 block truncate text-[11px] font-medium text-gray-600">
              {`Image ${index + 1}`}
            </span>
          </a>
        );
      })}
    </div>
  );
};

const formatVoteLabel = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const hasDisplayValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const formatReviewType = (value) => formatVoteLabel(value);

const formatVerifiedStatus = (value) => {
  if (String(value) === "1") return "Verified";
  if (String(value) === "0") return "Not Verified";
  return String(value || "-");
};

function ReviewList() {
  const antIcon = <LoadingOutlined style={{ fontSize: 52 }} spin />;

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState("");
  const [partnerReviews, setPartnerReviews] = useState([]);
  const [partnerReviewsLoading, setPartnerReviewsLoading] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);

    try {
      const result = await listAdminReviews();

      if (result?.status === 200) {
        setReviews(Array.isArray(result?.data) ? result.data : []);
      } else {
        toast.error(result?.message || "Failed to fetch reviews");
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const openReviewModal = async (review) => {
    if (!review?.id) return;

    setModalVisible(true);
    setSelectedReview(review);
    setPartnerReviews([]);
    setDetailLoading(true);

    try {
      const result = await getAdminReview(review.id);

      if (result?.status === 200 && result?.data && typeof result.data === "object") {
        setSelectedReview(result.data);
      } else if (result?.status !== 200) {
        toast.error(result?.message || "Failed to fetch review details");
      }
    } catch (error) {
      console.error("Error fetching review details:", error);
      toast.error("Failed to fetch review details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!selectedReview?.id) return;

    setStatusLoading(status);

    try {
      const result = await updateAdminReviewStatus(selectedReview.id, status);

      if (result?.status === 200) {
        toast.success(result?.message || "Review status updated");
        await fetchReviews();

        const detailResult = await getAdminReview(selectedReview.id);
        if (detailResult?.status === 200 && detailResult?.data && typeof detailResult.data === "object") {
          setSelectedReview(detailResult.data);
        } else {
          setSelectedReview((prev) => (prev ? { ...prev, status } : prev));
        }
      } else {
        toast.error(result?.message || "Failed to update review status");
      }
    } catch (error) {
      console.error("Error updating review status:", error);
      toast.error("Failed to update review status");
    } finally {
      setStatusLoading("");
    }
  };

  const loadPartnerReviews = async () => {
    const partnerId = selectedReview?.partner_id || selectedReview?.partner?.id;

    if (!partnerId) return;

    setPartnerReviewsLoading(true);

    try {
      const result = await getReviewsByPartnerId(partnerId);

      if (result?.status === 200) {
        setPartnerReviews(Array.isArray(result?.data) ? result.data : []);
      } else {
        toast.error(result?.message || "Failed to fetch partner reviews");
      }
    } catch (error) {
      console.error("Error fetching partner reviews:", error);
      toast.error("Failed to fetch partner reviews");
    } finally {
      setPartnerReviewsLoading(false);
    }
  };

  const filteredReviews = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return reviews;

    return reviews.filter((review) =>
      [
        review?.id,
        review?.customer_id,
        review?.partner_id,
        review?.service_id,
        getCustomerName(review),
        getPartnerName(review),
        getServiceName(review),
        getReviewTitle(review),
        getReviewText(review),
      ]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query))
    );
  }, [reviews, searchTerm]);

  const totalRecords = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedReviews = filteredReviews.slice(startIndex, endIndex);
  const displayedCount = totalRecords === 0 ? 0 : Math.min(totalRecords, endIndex);
  const selectedReviewStatus = getStatusMeta(selectedReview?.status);
  const voteSummaryEntries = Object.entries(selectedReview?.vote_summary || {});
  const selectedReviewDetails = [
    hasDisplayValue(getCustomerName(selectedReview)) && {
      label: "Customer",
      value: getCustomerName(selectedReview),
    },
    hasDisplayValue(getPartnerName(selectedReview)) && {
      label: "Partner",
      value: getPartnerName(selectedReview),
    },
    hasDisplayValue(getServiceName(selectedReview)) && {
      label: "Service",
      value: getServiceName(selectedReview),
    },
    hasDisplayValue(selectedReview?.review_type) && {
      label: "Review Type",
      value: formatReviewType(selectedReview?.review_type),
    },
    hasDisplayValue(selectedReview?.booking_id) && {
      label: "Booking ID",
      value: selectedReview?.booking_id,
    },
    hasDisplayValue(selectedReview?.user_id) && {
      label: "User ID",
      value: selectedReview?.user_id,
    },
    hasDisplayValue(selectedReview?.is_verified) && {
      label: "Verification",
      value: formatVerifiedStatus(selectedReview?.is_verified),
    },
    hasDisplayValue(selectedReview?.created_at) && {
      label: "Created At",
      value: formatReviewDate(selectedReview?.created_at, true),
    },
    hasDisplayValue(selectedReview?.updated_at) && {
      label: "Updated At",
      value: formatReviewDate(selectedReview?.updated_at, true),
    },
  ].filter(Boolean);

  return (
    <>
      {localStorage.getItem("Token") ? (
        <DashboardLayout>
          <DashboardNavbar />
          <Toaster position="top-center" reverseOrder={false} />

          {loading ? (
            <div className="flex h-[75vh] items-center justify-center">
              <Spin indicator={antIcon} />
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-6 py-4">
                <h2 className="text-base font-semibold text-gray-800">Review List</h2>
              </div>

              <div className="px-6 py-4">
                <input
                  type="text"
                  className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 lg:w-96"
                  placeholder="Search by customer, partner, service or review"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="mx-4 mb-3 rounded-md border border-gray-100">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  recordsPerPage={recordsPerPage}
                  onRecordsPerPageChange={(value) => {
                    setRecordsPerPage(value);
                    setCurrentPage(1);
                  }}
                  totalRecords={totalRecords}
                  count={displayedCount}
                  showFirstLastControls
                  compact
                />
              </div>

              <div className="mx-4 mb-4 overflow-x-auto">
                <table className="w-full text-left text-xs text-gray-600">
                  <thead className="bg-gray-50 text-[11px] uppercase text-gray-700">
                    <tr>
                      <th className="px-3 py-2.5">Sr No</th>
                      <th className="px-3 py-2.5">Customer</th>
                      <th className="px-3 py-2.5">Partner</th>
                      <th className="px-3 py-2.5">Service</th>
                      <th className="px-3 py-2.5">Rating</th>
                      <th className="px-3 py-2.5">Review</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-3 py-2.5">Date</th>
                      <th className="px-3 py-2.5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReviews.length > 0 ? (
                      paginatedReviews.map((review, index) => {
                        const statusMeta = getStatusMeta(review?.status);
                        const reviewText = getReviewText(review);

                        return (
                          <tr
                            key={review?.id ?? index}
                            className="border-b bg-white align-top hover:bg-gray-50"
                          >
                            <td className="whitespace-nowrap px-3 py-2">{startIndex + index + 1}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">
                                {getCustomerName(review) || "-"}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">
                                {getPartnerName(review) || "-"}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">
                                {getServiceName(review) || "-"}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <ReactStars
                                count={5}
                                value={getRatingValue(review)}
                                edit={false}
                                size={18}
                                color2="#fbbf24"
                              />
                            </td>
                            <td className="max-w-[260px] px-3 py-2">
                              {reviewText ? (
                                <span
                                  className="block overflow-hidden text-ellipsis"
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {reviewText}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`rounded px-2 py-1 text-[10px] font-semibold uppercase ${statusMeta.className}`}
                              >
                                {statusMeta.label}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {formatReviewDate(review?.created_at)}
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => openReviewModal(review)}
                                className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={9} className="py-6 text-center text-sm text-gray-500">
                          No reviews found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mx-4 mb-4 rounded-md border border-gray-100">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  recordsPerPage={recordsPerPage}
                  onRecordsPerPageChange={(value) => {
                    setRecordsPerPage(value);
                    setCurrentPage(1);
                  }}
                  totalRecords={totalRecords}
                  count={displayedCount}
                  showFirstLastControls
                  compact
                />
              </div>
            </div>
          )}

          <Modal
            title={`Review Details${selectedReview?.id ? ` #${selectedReview.id}` : ""}`}
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={null}
            width={780}
            zIndex={9999}
          >
            {detailLoading ? (
              <div className="flex h-48 items-center justify-center">
                <Spin indicator={antIcon} />
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 md:grid-cols-2">
                  <div>
                    <p className="text-[11px] uppercase text-gray-500">Rating</p>
                    <div className="pt-1">
                      <ReactStars
                        count={5}
                        value={getRatingValue(selectedReview)}
                        edit={false}
                        size={20}
                        color2="#fbbf24"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase text-gray-500">Status</p>
                    <span
                      className={`mt-1 inline-flex rounded px-2 py-1 text-[10px] font-semibold uppercase ${selectedReviewStatus.className}`}
                    >
                      {selectedReviewStatus.label}
                    </span>
                  </div>
                  {selectedReviewDetails.map((detailItem) => (
                    <div key={detailItem.label}>
                      <p className="text-[11px] uppercase text-gray-500">{detailItem.label}</p>
                      <p className="text-sm font-semibold text-gray-800">{detailItem.value}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <p className="text-[11px] uppercase text-gray-500">Title</p>
                  <p className="mt-1 rounded-lg border border-gray-100 bg-white p-3 text-sm text-gray-700">
                    {getReviewTitle(selectedReview) || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] uppercase text-gray-500">Review</p>
                  <p className="mt-1 rounded-lg border border-gray-100 bg-white p-3 text-sm text-gray-700">
                    {getReviewText(selectedReview) || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] uppercase text-gray-500">Media</p>
                  <div className="mt-1 rounded-lg border border-gray-100 bg-white p-3">
                    {renderReviewMedia(selectedReview?.media)}
                  </div>
                </div>

                <div>
                  <p className="text-[11px] uppercase text-gray-500">Vote Summary</p>
                  <div className="mt-1 rounded-lg border border-gray-100 bg-white p-3">
                    {voteSummaryEntries.length > 0 ? (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {voteSummaryEntries.map(([key, value]) => (
                          <div
                            key={String(key)}
                            className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                          >
                            <p className="text-[11px] uppercase text-gray-500">
                              {formatVoteLabel(key)}
                            </p>
                            <p className="mt-1 text-sm font-medium text-gray-800">
                              {String(value ?? "-")}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">No vote summary</span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleStatusUpdate("approved")}
                    disabled={statusLoading === "approved"}
                    className="rounded bg-green-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                  >
                    {statusLoading === "approved" ? "Updating..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStatusUpdate("rejected")}
                    disabled={statusLoading === "rejected"}
                    className="rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                  >
                    {statusLoading === "rejected" ? "Updating..." : "Reject"}
                  </button>
                  {(selectedReview?.partner_id || selectedReview?.partner?.id) && (
                    <button
                      type="button"
                      onClick={loadPartnerReviews}
                      disabled={partnerReviewsLoading}
                      className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-60"
                    >
                      {partnerReviewsLoading ? "Loading partner reviews..." : "Load Partner Reviews"}
                    </button>
                  )}
                </div>

                {partnerReviews.length > 0 && (
                  <div>
                    <p className="mb-2 text-[11px] uppercase text-gray-500">Partner Reviews</p>
                    <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-100">
                      {partnerReviews.map((reviewItem) => {
                        const partnerStatus = getStatusMeta(reviewItem?.status);

                        return (
                          <div
                            key={reviewItem?.id}
                            className="border-b border-gray-100 px-3 py-2 last:border-b-0"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-medium text-gray-800">
                                #{reviewItem?.id || "-"} {getCustomerName(reviewItem) || ""}
                              </p>
                              <span
                                className={`rounded px-2 py-1 text-[10px] font-semibold uppercase ${partnerStatus.className}`}
                              >
                                {partnerStatus.label}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-gray-600">
                              {getReviewText(reviewItem) || "-"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal>
        </DashboardLayout>
      ) : (
        <NontAuthorized401 />
      )}
    </>
  );
}

export default ReviewList;
